import { Tabs, Button } from 'antd';
import { Duration } from 'luxon';
import React, { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { DeleteOutlined } from '@ant-design/icons';

import { Carousel, Datability, DayRange, FontSize, FormWrapper, NumberField, Switch, Text } from '../../../WidgetConfiguratorComponents/FormGroups';
import IDatability from '../../types/IDatability';
import { IWidgetConfiguratorProps, SettingsChangeObjectFunction } from '../../types/IWidgetBlock';
import { TextTimeSeriesConfigBlockState, TextTimeSeriesSettings } from '../types';
import TextTable from '..';

type WidgetConfiguratorProps = IWidgetConfiguratorProps<TextTimeSeriesConfigBlockState>;

const WidgetConfigurator: React.FC<WidgetConfiguratorProps> = ({ setViewblockState, viewblockState, baseDatability }) => {
    const onSettingsChange: SettingsChangeObjectFunction<TextTimeSeriesSettings> = useCallback(
        (params) => {
            setViewblockState((prev) => ({
                ...prev,
                settings: { ...prev.settings, ...params },
            }));
        },
        [setViewblockState]
    );

    const onTimeRangeChange = (timeRange: [number, number]) => {
        const timeRangeISO = timeRange.map((days) => Duration.fromObject({ days }).toISO()).join('--');

        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                timeRange: timeRangeISO,
            },
            databilities: prev.databilities.map((datability) => ({
                ...datability,
                timeRange: timeRangeISO,
                data_structure: 'timeseries/point2d',
            })),
        }));
    };

    const onAddDatability = () => {
        setViewblockState((prev) => ({
            ...prev,
            databilities: [
                ...prev.databilities,
                {
                    ...baseDatability,
                    id: uuid(),
                    timeRange: prev.settings.timeRange,
                    capabilityId: '',
                    datasetId: '',
                    data_structure: 'timeseries/point2d',
                },
            ],
            settings: {
                ...prev.settings,
                labelDatabilities: [...prev.settings.labelDatabilities, ''],
            },
        }));
    };

    const onRemoveDatability = (databilityIndex: number) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                labelDatabilities: prev.settings.labelDatabilities.filter((_l, i) => i !== databilityIndex),
            },
            databilities: prev.databilities.filter((_d, i) => i !== databilityIndex),
        }));
    };

    const onChangeDatability = (newDatability: IDatability) => {
        setViewblockState((prev) => ({
            ...prev,
            databilities: prev.databilities.map((datability) => {
                if (datability.id === newDatability.id) {
                    return newDatability;
                } else {
                    return datability;
                }
            }),
        }));
    };

    const onDatabilityLabelChange = (databilityIndex: number, newLabel: string) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                labelDatabilities: prev.settings.labelDatabilities.map((label, labelIndex) => {
                    if (labelIndex === databilityIndex) {
                        return newLabel;
                    } else {
                        return label;
                    }
                }),
            },
        }));
    };

    const onUseFixedOffsetChange = (useFixedOffset: boolean) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                useFixedOffset,
            },
        }));
    };

    const onOffsetLeftChange = (offsetLeft: number) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                offsetLeft,
            },
        }));
    };

    const timeRangeInDays = viewblockState.settings.timeRange.split('--').map((durationISOString) => Duration.fromISO(durationISOString).days);

    return (
        <Tabs style={{ maxWidth: '100%' }}>
            <Tabs.TabPane tab='General settings' key='1'>
                <FormWrapper>
                    <DayRange dayRange={{ min: timeRangeInDays[0], max: timeRangeInDays[1] }} onChange={onTimeRangeChange} />
                    <FontSize fontSize={viewblockState.settings.fontSize} onChange={(fontSize: number) => onSettingsChange({ fontSize })} />
                    <Switch
                        label='Display table heading'
                        value={viewblockState.settings.displayTableHeading}
                        onChange={(displayTableHeading) => onSettingsChange({ displayTableHeading })}
                    />
                    <Switch label='Use fixed offset' value={viewblockState.settings.useFixedOffset} onChange={onUseFixedOffsetChange} />
                    {viewblockState.settings.useFixedOffset && (
                        <NumberField label='Left offset' value={viewblockState.settings.offsetLeft} onChange={onOffsetLeftChange} step={1} />
                    )}
                </FormWrapper>
            </Tabs.TabPane>
            <Tabs.TabPane tab='Data definition' key='2'>
                <FormWrapper>
                    <Carousel label='Data' addItem={() => onAddDatability()}>
                        {viewblockState.databilities.map((datability, databilityIndex) => (
                            <React.Fragment key={datability.id}>
                                <Datability
                                    onChangeDatability={onChangeDatability}
                                    unit='utc'
                                    onUnitChange={() => false}
                                    dataType={[`text`]}
                                    datability={datability}
                                    disableInterpolation={true}
                                    dataStructures={TextTable.dataStructures}
                                    onInterpolationChange={() => false}
                                />
                                <Text
                                    label='Label'
                                    value={viewblockState.settings.labelDatabilities[databilityIndex]}
                                    onChange={(newLabel: string) => onDatabilityLabelChange(databilityIndex, newLabel)}
                                />
                                <div style={{ gridColumn: '1 / 6', gridRow: 7, textAlign: 'center' }}>
                                    <hr />
                                    <Button onClick={() => onRemoveDatability(databilityIndex)} danger icon={<DeleteOutlined />}>
                                        Delete
                                    </Button>
                                </div>
                            </React.Fragment>
                        ))}
                    </Carousel>
                </FormWrapper>
            </Tabs.TabPane>
        </Tabs>
    );
};

export default WidgetConfigurator;
